import { BackIcon } from '@euler/app/components/icons/BackIcon';
import { Typography } from '@euler/app/components/typography';
import { kDefaultVehicleImage } from '@euler/assets';
import { AsideContext, useIsAsideLayout } from '@euler/components/Aside';
import { Img } from '@euler/components/image/AdvancedImage';
import { HStack } from '@euler/components/layout/Stack';
import { PredefinedHitSlops } from '@euler/functions/predefinedHitSlops';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import Constants from 'expo-constants';
import { nanoid } from 'nanoid/non-secure';
import { memo, useCallback, useContext, useRef } from 'react';
import {
  StyleProp,
  StyleSheet,
  TouchableOpacity,
  View,
  ViewStyle,
  useWindowDimensions,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Svg, { Defs, Ellipse, LinearGradient, Stop } from 'react-native-svg';

const kContentHeight = 36;
const kMargin = 44;
const kCardHeight = 70;

export const PreviewOrderNavHeader = memo(
  ({
    title,
    licensePlateNo,
    mileage,
    vehicleName,
    vehicleImageUrl,
  }: {
    title?: string;
    licensePlateNo: string;
    mileage: number;
    vehicleName: string;
    vehicleImageUrl?: string;
  }) => {
    const isAside = useIsAsideLayout();
    const { top } = useSafeAreaInsets();
    const { rpx } = useResponsiveContext();

    const navigation = useNavigation<StackNavigationProp<any>>();

    const onBackButtonPress = useCallback(() => {
      navigation.goBack();
    }, [navigation]);

    return (
      <View
        css={`
          padding-top: ${top}px;
        `}
      >
        <View
          style={StyleSheet.absoluteFill}
          css={`
            overflow: hidden;
          `}
        >
          <Shade />
        </View>

        <View
          css={`
            height: ${rpx(kContentHeight) +
            (isAside ? Constants.statusBarHeight : 0)}px;
            padding-top: ${isAside ? Constants.statusBarHeight : 0}px;
            justify-content: center;
          `}
        >
          <Typography.Label align="center" medium size={rpx(18)} color="#fff">
            {title ?? '订单信息'}
          </Typography.Label>
          <TouchableOpacity
            hitSlop={PredefinedHitSlops.large}
            onPress={onBackButtonPress}
            css={`
              position: absolute;
              left: 0;
              top: ${isAside ? Constants.statusBarHeight : 0}px;
              bottom: 0;
              justify-content: center;
              align-items: center;
            `}
          >
            <BackIcon fill="#fff" size={rpx(56)} />
          </TouchableOpacity>
        </View>
        <View
          css={`
            background-color: #fff;
            border-radius: ${rpx(6)}px;
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
            margin: 0 ${rpx(15)}px;
            height: ${rpx(100)}px;
            padding: ${rpx(20)}px;
            box-shadow: 0 0 5px rgba(0, 0, 0, 0.25);
            margin-top: ${rpx(8)}px;
          `}
        >
          <View>
            <Typography.Label medium size={rpx(18)}>
              {vehicleName}
            </Typography.Label>
            <HStack
              justifyContent="flex-start"
              css={`
                margin-top: ${rpx(15)}px;
              `}
            >
              <View
                css={`
                  background-color: rgb(32, 127, 231);
                  border-radius: ${rpx(2)}px;
                  padding: 0 ${rpx(6)}px;
                  justify-content: center;
                  align-items: center;
                `}
              >
                <Typography.Label regular size={rpx(14)} color="#fff">
                  {licensePlateNo}
                </Typography.Label>
              </View>
              {mileage > 0 && (
                <Typography.Label
                  color="#777"
                  size={rpx(14)}
                  regular
                  css={`
                    margin-left: ${rpx(10)}px;
                  `}
                >
                  {`${mileage}KM`}
                </Typography.Label>
              )}
            </HStack>
          </View>
          <HStack
            css={`
              width: 30%;
              justify-content: flex-end;
            `}
          >
            <Img
              uri={vehicleImageUrl}
              source={vehicleImageUrl ? undefined : kDefaultVehicleImage}
              resizeMode="contain"
              css={`
                width: 100%;
                height: ${rpx(98)}px;
              `}
            />
          </HStack>
        </View>
      </View>
    );
  },
);

const Shade = memo(({ style }: { style?: StyleProp<ViewStyle> }) => {
  const gradientId = useRef(nanoid()).current;
  const aside = useContext(AsideContext);
  const dimens = useWindowDimensions();
  const { top } = useSafeAreaInsets();
  const { rpx } = useResponsiveContext();
  const width = aside?.width ?? dimens.width;
  const factor = width / 428;
  const [w, h] = [rpx(670) * factor, rpx(522) * factor];
  const l = (width - w) / 2;
  const t =
    top +
    (aside ? Constants.statusBarHeight : 0) +
    rpx(kContentHeight + kMargin + kCardHeight) -
    h;
  return (
    <Svg
      width={w}
      height={h}
      viewBox="0 0 670 522"
      fill="none"
      style={style}
      pointerEvents="none"
      css={`
        position: absolute;
        left: ${l}px;
        top: ${t}px;
      `}
    >
      <Defs>
        <LinearGradient
          id={gradientId}
          x1="101.839355"
          y1="1035.843750"
          x2="619.080811"
          y2="-575.015198"
          gradientUnits="userSpaceOnUse"
        >
          <Stop offset={0} stopColor="#006BE4" />
          <Stop offset={1} stopColor="#5BA0E0" />
        </LinearGradient>
      </Defs>
      <Ellipse
        id="Ellipse 7"
        cx="335.000000"
        cy="261.000000"
        rx="335.000000"
        ry="261.000000"
        fill={`url(#${gradientId})`}
      />
    </Svg>
  );
});
